<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Modal, Container, Text, Button } from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores';
	import { selectedNode, flowEditorState } from 'src/stores/flow_editor';
	import { getEffectiveNodeDisplayLabel } from './helpers/LabelGenerator';
	import PropertyPanel from './PropertyPanel.svelte';
	import { trackFlowUxEvent } from '../core/ux/flow-ux-telemetry';

	export let isMobile = false;

	$: isOpen = $selectedNode !== null && !!$flowEditorState.isEditorOpen;
	$: title = $selectedNode
		? (() => {
				if ($selectedNode.type === 'note') return 'Note Settings';
				$devices;
				return `${getEffectiveNodeDisplayLabel($selectedNode)} Settings`;
			})()
		: 'Node Settings';
	$: hasPendingChanges = !!$flowEditorState.pendingNode;
	let openedAt = 0;
	let trackedNodeKey = '';
	$: if (isOpen && $selectedNode) {
		const key = String($selectedNode.data.paletteKey || $selectedNode.type);
		if (trackedNodeKey !== key) {
			trackedNodeKey = key;
			openedAt = Date.now();
			trackFlowUxEvent({
				name: 'configuration_opened',
				surface: 'inspector',
				paletteKey: key,
				elapsedMs: 0,
			});
		}
	}

	function trackConfiguration(name: 'configuration_applied' | 'configuration_abandoned') {
		if (!trackedNodeKey) return;
		trackFlowUxEvent({
			name,
			surface: 'inspector',
			paletteKey: trackedNodeKey,
			elapsedMs: Math.max(0, Date.now() - openedAt),
		});
		trackedNodeKey = '';
	}

	function handleClose() {
		trackConfiguration('configuration_abandoned');
		flowEditorState.revertNodeToOriginal();
		flowEditorState.setEditorOpen(false);
		flowEditorState.selectNode(null);
	}

	function handleSave() {
		trackConfiguration('configuration_applied');
		// Inspectors update the selected node as the user works. The pending snapshot is
		// retained only so Cancel can restore the original value.
		flowEditorState.clearPendingNode();
		flowEditorState.setEditorOpen(false);
		flowEditorState.selectNode(null);
	}

	function handleCancel() {
		trackConfiguration('configuration_abandoned');
		flowEditorState.revertNodeToOriginal();
		flowEditorState.setEditorOpen(false);
		flowEditorState.selectNode(null);
	}

	function handleDelete() {
		trackConfiguration('configuration_abandoned');
		const nodeId = $selectedNode?.id;
		if (nodeId) flowEditorState.removeNode(nodeId);
		flowEditorState.clearPendingNode();
		flowEditorState.setEditorOpen(false);
	}
</script>

<Modal
	showModal={isOpen}
	{title}
	type={isMobile ? 'fullScreen' : 'default'}
	backdropClose={false}
	on:close={handleClose}
>
	<svelte:fragment slot="content">
		<Container padding="0">
			{#if $selectedNode}
				<PropertyPanel
					showScene={false}
					showValidation={false}
					showHistory={false}
					compactNodeEditor={true}
				/>
			{:else}
				<div style="padding: 1rem;">
					<Text variant="body" size="m">No node selected.</Text>
				</div>
			{/if}
		</Container>
	</svelte:fragment>
	<div slot="footer" class="modal-footer-actions">
		<Button variant="danger" on:click={handleDelete}>Delete node</Button>
		<div class="modal-footer-primary">
			<Button variant="secondary" on:click={handleCancel}>
				{$format('label_cancel', { default: 'Cancel' })}
			</Button>
			<Button variant="primary" on:click={handleSave}>
				{$format('label_apply', { default: 'Apply' })}
			</Button>
		</div>
	</div>
</Modal>

<style>
	.modal-footer-actions {
		display: flex;
		gap: 1rem;
		justify-content: space-between;
		align-items: center;
	}

	.modal-footer-primary {
		display: flex;
		gap: 0.75rem;
	}

	@media (max-width: 639px) {
		.modal-footer-actions,
		.modal-footer-primary {
			align-items: stretch;
			flex-direction: column-reverse;
			width: 100%;
		}
	}
</style>
